import { useEffect, useState } from "react";
import { WindowMock } from "./WindowMock";
import { APP_ICONS } from "./appIcons";
import styles from "./Layouts.module.css";

/**
 * Layouts — single faithful mockup of the actual Layouts app builder.
 *
 * Shows the real two-pane shape the visitor would see if they installed
 * Atlas Desk: left sidebar with named layouts (one marked default,
 * another currently selected), right pane with a snap-to-grid editor
 * canvas. Tiles on the canvas read as "windows you've placed" — each
 * one labelled with an app name so the metaphor is unambiguous.
 *
 * The mockup is gently animated: every CYCLE_MS the selected sidebar
 * row advances to the next layout, and the canvas tiles slide / fade
 * into the new arrangement. Each tile gets a stable React key keyed
 * off its app id, so tiles shared between two layouts move smoothly
 * (CSS transitions on top/left/width/height) while tiles unique to a
 * layout fade in or out.
 */

type TileId = "notes" | "stickies" | "music" | "browser" | "movies" | "kepler";

interface TilePos {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface LayoutDef {
  name: string;
  tiles: Partial<Record<TileId, TilePos>>;
}

const TILE_META: Record<TileId, { label: string; className: string }> = {
  notes: { label: "Notes", className: "tileNotes" },
  stickies: { label: "Stickies", className: "tileStickies" },
  music: { label: "Music", className: "tileMusic" },
  browser: { label: "Browser", className: "tileBrowser" },
  movies: { label: "Movies", className: "tileMovies" },
  kepler: { label: "Kepler", className: "tileKepler" },
};

const LAYOUTS: LayoutDef[] = [
  {
    name: "Writing",
    tiles: {
      notes: { top: 6, left: 4, width: 56, height: 80 },
      stickies: { top: 6, left: 64, width: 32, height: 38 },
      music: { top: 50, left: 64, width: 32, height: 36 },
    },
  },
  {
    name: "Listening",
    tiles: {
      music: { top: 6, left: 4, width: 70, height: 80 },
      stickies: { top: 6, left: 78, width: 18, height: 30 },
    },
  },
  {
    name: "Research",
    tiles: {
      browser: { top: 6, left: 4, width: 44, height: 62 },
      kepler: { top: 6, left: 52, width: 44, height: 62 },
      notes: { top: 72, left: 4, width: 92, height: 14 },
    },
  },
  {
    name: "Off-hours",
    tiles: {
      movies: { top: 6, left: 4, width: 72, height: 80 },
      stickies: { top: 14, left: 80, width: 16, height: 28 },
    },
  },
];

const TILE_IDS: TileId[] = [
  "notes",
  "stickies",
  "music",
  "browser",
  "movies",
  "kepler",
];

const CYCLE_MS = 4000;

/**
 * Pre-compute each tile's "first appearance" position across all
 * layouts. Tiles render from frame 1 — those not in the initial
 * layout sit at their future position with opacity 0, so when their
 * layout arrives they just fade in cleanly rather than sliding in
 * from an arbitrary off-screen point.
 */
const INITIAL_POS: Partial<Record<TileId, TilePos>> = (() => {
  const acc: Partial<Record<TileId, TilePos>> = {};
  for (const layout of LAYOUTS) {
    for (const id of TILE_IDS) {
      if (!acc[id] && layout.tiles[id]) {
        acc[id] = layout.tiles[id];
      }
    }
  }
  return acc;
})();

export function Layouts() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [positions, setPositions] =
    useState<Partial<Record<TileId, TilePos>>>(INITIAL_POS);

  // Auto-cycle through the layouts on a timer.
  useEffect(() => {
    const id = globalThis.setInterval(() => {
      setActiveIndex((i) => (i + 1) % LAYOUTS.length);
    }, CYCLE_MS);
    return () => globalThis.clearInterval(id);
  }, []);

  // When the active layout changes, capture the new positions for
  // tiles that ARE in it. Tiles NOT in the new layout keep their
  // last known position so they fade out in place rather than
  // teleporting somewhere.
  useEffect(() => {
    setPositions((prev) => {
      const next = { ...prev };
      const active = LAYOUTS[activeIndex];
      if (!active) return prev;
      for (const id of TILE_IDS) {
        const spec = active.tiles[id];
        if (spec) next[id] = spec;
      }
      return next;
    });
  }, [activeIndex]);

  const active = LAYOUTS[activeIndex]!;

  return (
    <section
      id="layouts"
      className={styles.section}
      aria-labelledby="layouts-title"
    >
      <div className={`atlasContainer ${styles.inner}`}>
        <header className={styles.header}>
          <span className="atlasEyebrow">Layouts</span>
          <h2 id="layouts-title" className={styles.title}>
            Save a setup.
            <br />
            <span className={styles.titleAccent}>Restore it instantly.</span>
          </h2>
          <p className={styles.lede}>
            A layout is whatever you make it — any apps, any arrangement,
            any size.<br/>Build as many as you need, and switch whenever
            the day shifts gears.
          </p>
        </header>

        <div className={styles.showcase}>
          <WindowMock
            title="LAYOUTS"
            iconSvg={APP_ICONS.layouts}
            bodyPadding="flush"
          >
            <div className={styles.builder}>
              {/* Left rail: the cycling "active" highlight moves through
                  these as the timer fires. Writing keeps the default pill
                  regardless of which row is currently active. */}
              <aside className={styles.sidebar}>
                <ul className={styles.layoutList}>
                  {LAYOUTS.map((layout, i) => {
                    const isActive = i === activeIndex;
                    return (
                      <li
                        key={layout.name}
                        className={
                          isActive
                            ? `${styles.layoutItem} ${styles.layoutItemActive}`
                            : styles.layoutItem
                        }
                      >
                        <span className={styles.layoutName}>{layout.name}</span>
                      </li>
                    );
                  })}
                </ul>
              </aside>

              {/* Right pane: editor canvas. Apply button floats bottom-
                  right inside the canvas frame rather than sitting in a
                  toolbar; the canvas fills the entire pane. */}
              <main className={styles.editor}>
                <div className={styles.canvas} aria-hidden>
                  <div className={styles.canvasGrid} />
                  {TILE_IDS.map((id) => {
                    const pos = positions[id];
                    if (!pos) return null;
                    const meta = TILE_META[id];
                    const visible = !!active.tiles[id];
                    return (
                      <span
                        key={id}
                        className={`${styles.canvasTile} ${styles[meta.className]}`}
                        style={{
                          top: `${pos.top}%`,
                          left: `${pos.left}%`,
                          width: `${pos.width}%`,
                          height: `${pos.height}%`,
                          opacity: visible ? 1 : 0,
                        }}
                      >
                        {meta.label}
                      </span>
                    );
                  })}

                  <span className={styles.applyFloat}>Apply →</span>
                </div>
              </main>
            </div>
          </WindowMock>
        </div>
      </div>
    </section>
  );
}
